// routes/ProtectedRoute.jsx
import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { COOKIE_EXPIRE } from "../utils/api";

const ROLE_REDIRECT = {
  1: "/userprofile",
  2: "/teacher-profile",
  3: "/people",
};

const ProtectedRoute = ({ allowedRoles = [] }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    let logoutTimer;

    const logoutUser = () => {
      localStorage.removeItem("access_token");
      localStorage.removeItem("role");
      setIsAuthenticated(false);
      setAuthChecked(true); // FIX: was missing, caused infinite Loading...
      navigate("/", { replace: true });
    };

    const checkAuth = async () => {
      const token = localStorage.getItem("access_token");
      const role = Number(localStorage.getItem("role")) || null;
      setUserRole(role);

      // ===== JWT CHECK =====
      if (token) {
        try {
          const parts = token.split(".");
          if (parts.length !== 3) throw new Error("Invalid JWT");

          // FIX: replace URL-safe base64 chars before atob()
          // Your old code used atob(payloadBase64) directly — this throws
          // on tokens with - or _ characters (URL-safe base64 format)
          const payload = JSON.parse(
            atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
          );

          const currentTime = Math.floor(Date.now() / 1000);

          if (!payload.exp || payload.exp <= currentTime) {
            logoutUser();
            return;
          }

          const remainingTime = (payload.exp - currentTime) * 1000;
          logoutTimer = setTimeout(logoutUser, remainingTime);

          if (allowedRoles.length === 0 || allowedRoles.includes(role)) {
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
          }

          setAuthChecked(true);
          return;

        } catch (err) {
          console.error("JWT decode failed:", err);
          // Don't logoutUser() here — fall through to cookie check
          // in case the token is malformed but cookie is still valid
          localStorage.removeItem("access_token");
        }
      }

      // ===== COOKIE FALLBACK =====
      // Reached when: no token, or JWT decode failed
      try {
        const resp = await axios.get(COOKIE_EXPIRE, { withCredentials: true });

        // Also sync role from cookie response in case localStorage is stale
        const cookieRole = Number(resp.data?.role_id) || role;
        const cookieToken = resp.data?.access_token;

        if (cookieRole) {
          localStorage.setItem("role", cookieRole.toString());
          setUserRole(cookieRole);
        }
        if (cookieToken) {
          localStorage.setItem("access_token", cookieToken);
        }

        if (allowedRoles.length === 0 || allowedRoles.includes(cookieRole)) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }

      } catch {
        setIsAuthenticated(false);
      } finally {
        setAuthChecked(true);
      }
    };

    checkAuth();

    return () => logoutTimer && clearTimeout(logoutTimer);
  }, [location.pathname]); // FIX: removed allowedRoles + navigate — caused infinite re-runs

  if (!authChecked) return <div>Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/" replace />;

  if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
    return <Navigate to={ROLE_REDIRECT[userRole] || "/"} replace />;
  }

  return <Outlet />;
};

export default ProtectedRoute;